import { useEffect } from "react";
import { X } from "lucide-react";

export function Card({ children, className = "" }) {
  return <div className={`rounded-xl bg-[#151D2A] border border-white/10 ${className}`}>{children}</div>;
}

export function KPICard({ label, value, sub, accent = "#1D4ED8", icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="relative p-5 rounded-xl bg-[#151D2A] border border-white/10 shadow-lg hover:border-white/20 transition-all overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: accent }} />
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">{label}</div>
        {Icon && <Icon size={16} style={{ color: accent }} />}
      </div>
      <div className="font-mono-metric text-2xl font-bold text-white mt-3">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

const HEALTH = {
  optimal: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  warning: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  critical: "bg-rose-500/15 text-rose-400 border-rose-500/30",
};
const DOT = { optimal: "bg-emerald-400", warning: "bg-amber-400", critical: "bg-rose-400" };

export function HealthBadge({ health }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border ${HEALTH[health] || HEALTH.optimal}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${DOT[health] || DOT.optimal}`} /> {health}
    </span>
  );
}

const STATUS_COLORS = {
  Active: "emerald", approved: "emerald", completed: "emerald", Won: "emerald", closed_won: "emerald", paid: "emerald",
  assigned: "slate", Draft: "slate", New: "slate", pending: "amber", Scheduled: "blue", scheduled: "blue",
  in_progress: "blue", "In Progress": "blue", under_review: "amber", submitted: "amber", Proposal: "blue",
  rejected: "rose", Lost: "rose", cancelled: "rose", suspended: "rose", Paused: "amber", processing: "blue",
  "Meeting Booked": "blue", Qualified: "blue", Interested: "blue", Contacted: "slate", high: "rose", medium: "amber", low: "slate",
};
export function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || "slate";
  const map = {
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    rose: "bg-rose-500/10 text-rose-400 border-rose-500/20",
    slate: "bg-slate-500/10 text-slate-300 border-slate-500/20",
  };
  return <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full border ${map[c]}`}>{String(status).replace(/_/g, " ")}</span>;
}

export function EmptyState({ title, desc, action }) {
  return (
    <div className="text-center py-16 px-6">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-white/5 grid place-items-center text-slate-500 text-2xl">∅</div>
      <h3 className="font-head text-lg font-semibold text-white mt-4">{title}</h3>
      {desc && <p className="text-slate-400 text-sm mt-1 max-w-md mx-auto">{desc}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Btn({ children, variant = "primary", className = "", ...props }) {
  const styles = {
    primary: "bg-[#1D4ED8] text-white hover:bg-[#1E40AF]",
    ghost: "bg-white/5 text-slate-200 hover:bg-white/10 border border-white/10",
    danger: "bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 border border-rose-500/30",
  };
  return <button {...props} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${styles[variant]} ${className}`}>{children}</button>;
}

export function Modal({ open, onClose, title, children, wide }) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 sm:p-8 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-3xl" : "max-w-lg"} rounded-2xl bg-[#151D2A] border border-white/10 shadow-2xl my-8`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-head text-lg font-semibold text-white">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function Input({ label, className = "", ...props }) {
  return (
    <div className={className}>
      {label && <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</label>}
      <input {...props} className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0B0F19] border border-white/10 text-white text-sm focus:border-[#1D4ED8] outline-none" />
    </div>
  );
}

export function Select({ label, children, className = "", ...props }) {
  return (
    <div className={className}>
      {label && <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</label>}
      <select {...props} className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0B0F19] border border-white/10 text-white text-sm focus:border-[#1D4ED8] outline-none">{children}</select>
    </div>
  );
}

export function Th({ children }) { return <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{children}</th>; }
export function Td({ children, className = "" }) { return <td className={`px-4 py-3 text-sm text-slate-200 ${className}`}>{children}</td>; }

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="font-head text-2xl font-bold text-white tracking-tight">{title}</h1>
        {subtitle && <p className="text-slate-400 text-sm mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
